import React from "react";
import { ALLERGY_TAGS } from "@/lib/format";

/**
 * A table seen from above, with everyone's name in their seat.
 *
 * This is the view that answers "who am I sitting next to?" — the question a
 * seating chart exists for. Names stay horizontal rather than rotating with the
 * circle: radial text looks elegant and is genuinely hard to read at a glance.
 */
export const RoundTable = ({
  table,
  guests = [],
  conflictIds,
  selectedGuestId,
  onSelectGuest,
  size = 400,
  showName = true,
}) => {
  const center = size / 2;
  const tableRadius = size * 0.24;
  const seatRadius = size * 0.29;
  const labelRadius = size * 0.325;
  const seats = Math.max(guests.length, table?.capacity || 0, 1);

  const conflicted = conflictIds || new Set();
  const over = (table?.seats_used || 0) > (table?.capacity || 0);

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      className="select-none"
      role="img"
      aria-label={`${table?.title || `Table ${table?.table}`}, ${guests.length} invités`}
      data-testid={`round-table-${table?.table}`}
    >
      <circle
        cx={center}
        cy={center}
        r={tableRadius}
        fill="#ffffff"
        stroke={over ? "var(--danger)" : "var(--line)"}
        strokeWidth={1.2}
      />
      <circle
        cx={center}
        cy={center}
        r={tableRadius - 7}
        fill="none"
        stroke="var(--champagne, #E6DED3)"
        strokeWidth={0.6}
      />

      {showName ? (
        <>
          <text
            x={center}
            y={center - 6}
            textAnchor="middle"
            className="font-num"
            style={{ fontSize: size * 0.028, fill: "var(--gild)", letterSpacing: "0.14em" }}
          >
            TABLE {table?.table}
          </text>
          <text
            x={center}
            y={center + 14}
            textAnchor="middle"
            className="font-display"
            style={{ fontSize: size * 0.045, fill: "var(--ink)", fontWeight: 600 }}
          >
            {(table?.title || "").slice(0, 22)}
          </text>
          <text
            x={center}
            y={center + 32}
            textAnchor="middle"
            className="font-num"
            style={{ fontSize: size * 0.026, fill: "var(--muted-ink)" }}
          >
            {table?.seats_used}/{table?.capacity}
          </text>
        </>
      ) : null}

      {Array.from({ length: seats }).map((_, index) => {
        const angle = (index / seats) * Math.PI * 2 - Math.PI / 2;
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const guest = guests[index];
        const sx = center + cos * seatRadius;
        const sy = center + sin * seatRadius;
        const lx = center + cos * labelRadius;
        const ly = center + sin * labelRadius;
        const anchor = cos > 0.2 ? "start" : cos < -0.2 ? "end" : "middle";
        const isConflict = guest && conflicted.has?.(guest.id);
        const isSelected = guest && guest.id === selectedGuestId;
        const allergy = (guest?.diet_tags || []).some((tag) => ALLERGY_TAGS.has(tag));

        return (
          <g
            key={index}
            onClick={() => guest && onSelectGuest?.(guest)}
            style={{ cursor: guest && onSelectGuest ? "pointer" : "default" }}
            data-testid={guest ? `round-seat-${guest.id}` : `round-seat-empty-${index}`}
          >
            <circle
              cx={sx}
              cy={sy}
              r={size * 0.018}
              fill={
                !guest
                  ? "transparent"
                  : isConflict
                    ? "var(--danger)"
                    : isSelected
                      ? "var(--sage-dark)"
                      : "var(--sage)"
              }
              stroke={guest ? "none" : "var(--line)"}
              strokeWidth={1}
              strokeDasharray={guest ? undefined : "2 2"}
            />
            {guest ? (
              <text
                x={lx}
                y={ly + size * 0.011}
                textAnchor={anchor}
                style={{
                  fontSize: size * 0.032,
                  fill: isConflict ? "var(--danger)" : "var(--ink)",
                  fontWeight: isSelected || isConflict ? 700 : 400,
                }}
              >
                {guest.name}
                {allergy ? " ⚠" : ""}
                {!guest.responded ? " ·" : ""}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
};

export default RoundTable;
